import cx from 'classnames'
import dayjs from 'dayjs'
import { useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'

import { PlatePage_CulturePlate } from '~/api/backend/pages/platePageAPI'
import Button from '~/components/buttons/Button'
import MicroplateIcon from '~/components/icons/MicroplateIcon'
import PageNotFound from '~/components/PageNotFound'
import PlateHero from '~/components/PlateHero'
import PopupMenu from '~/components/PopupMenu'
import AppToaster from '~/components/Toaster'
import ZeroWidthSpaceToken from '~/components/ZeroWidthSpaceToken'
import { analytics } from '~/core/analytics'
import { timestampOrRelativeDay } from '~/demoControls/converters/demoTimepoint'
import { useDemoMutation, useDemoQuery } from '~/demoControls/DemoContext'
import DemoDataRequiredFullPageError from '~/demoControls/DemoDataRequiredFullPageError'
import PlateConfluenceGraphSidebar from '~/pages/plate/PlateConfluenceGraphSidebar'
import PlateMetadataSidebar from '~/pages/plate/PlateMetadataSidebar'

import {
  initialDemoData,
  postClonePickingDemoData,
} from '../data/generateClePipelinePlates'
import BreadcrumbsCle2025 from './BreadcrumbsCle2025'
import cs from './plate_cle_2025.scss'

export default function PlatePageCle2025({
  id,
  enableSelection = false,
  routePrefix = '',
}: {
  id: string
  enableSelection?: boolean
  routePrefix?: string
}) {
  const variables = { id }
  const demoData = useDemoQuery(
    routePrefix ? 'cle' : 'monitor',
    'PlatePage',
  )?.(variables)
  if (!demoData) {
    return <DemoDataRequiredFullPageError />
  }
  if (!demoData.culturePlate) {
    return <PageNotFound />
  }
  return (
    <Content
      plate={demoData.culturePlate}
      enableSelection={enableSelection}
      routePrefix={routePrefix}
    />
  )
}

function Content({
  plate,
  enableSelection,
  routePrefix,
}: {
  plate: PlatePage_CulturePlate
  enableSelection?: boolean
  routePrefix: string
}) {
  const [latestPlateObservationTime] = plate.plateObservationHistory
    .map(obs => obs.timestamp)
    .sort()
    .slice(-1) as [string | undefined]

  useEffect(() => {
    analytics.page('Monitor', 'Plate', {
      plateID: plate.id,
      numPlateObservations: plate.plateObservationHistory.length,
      latestPlateObservationTime: latestPlateObservationTime ?? null,
    })
  }, [])

  const time = latestPlateObservationTime ? dayjs(latestPlateObservationTime) : null
  const isThisYear = time?.year() === dayjs().year()

  const [selectionState, setSelectionState] = useState<{
    selectedWells: string[] | undefined
  }>({
    selectedWells: undefined,
  })
  const history = useHistory()

  const handleSelectWellsToPassage = () => {
    const sortedTimepoints = initialDemoData.timepoints
      ?.toSorted(
        (b, a) =>
          dayjs(timestampOrRelativeDay(a.timestamp)).unix() +
          Number(a.confluence) -
          dayjs(timestampOrRelativeDay(b.timestamp)).unix() -
          Number(b.confluence),
      )
      .slice(0, 72)
      .map(a => a.wellPosition)
    setSelectionState({ selectedWells: sortedTimepoints })
  }

  const [isDropdownOpen, setDropdownOpen] = useState(false)
  const plateActionsOptions = [
    {
      label: 'Select Wells to Passage...',
      action: handleSelectWellsToPassage,
    },
    {
      label: 'Mark Wells Contaminated...',
      action: () => {},
    },
    {
      label: 'Mark Wells Empty...',
      action: () => {},
    },
    {
      label: 'Mark Wells Multi-clonal...',
      action: () => {},
    },
  ]

  const handleCancelPassage = () => {
    setSelectionState({ selectedWells: undefined })
  }

  const updateDemoPlatesForPassage = useDemoMutation(
    'cle',
    (demoData, { selectedWells }: { selectedWells?: string[] }) => {
      if (!selectedWells?.length) {
        return demoData
      } else {
        return postClonePickingDemoData
      }
    },
  )

  const handleSubmitPassage = () => {
    AppToaster.show({
      message: `Successfully scheduled ${selectionState.selectedWells?.length} wells for passage.`,
      intent: 'success',
    })
    setSelectionState({ selectedWells: undefined })
    updateDemoPlatesForPassage({ selectedWells: selectionState.selectedWells })
    history.push('/cle-2025/pipeline')
  }

  const handleWellToggle = (well: string) => {
    if (selectionState.selectedWells === undefined) {
      return
    }
    setSelectionState(prevState => {
      const selectedWells = prevState.selectedWells || []
      if (selectedWells.includes(well)) {
        return { selectedWells: selectedWells.filter(w => w !== well) }
      } else {
        return { selectedWells: [...selectedWells, well] }
      }
    })
  }

  return (
    <div>
      <BreadcrumbsCle2025
        plate={{ id: plate.id, data: plate }}
        routePrefix={routePrefix}
      />

      <div className={cs.main}>
        <div className={cs.title}>
          {plate.barcode}
          <ZeroWidthSpaceToken />
          <PlateStatus plate={plate} />
        </div>

        {selectionState.selectedWells === undefined ? (
          <div className={cs.heroToolbar}>
            <div className={cs.heroLabel}>
              {time ? (
                <>
                  {time.format(isThisYear ? 'MMM D' : 'MMM D, YYYY')}&ensp;
                  {time.format('h:mm A')} ({time.fromNow()})
                </>
              ) : (
                <>No images yet.</>
              )}
            </div>
            {enableSelection && (
              <PopupMenu
                options={plateActionsOptions}
                trigger={
                  <a
                    className={cs.plateActions}
                    onClick={() => setDropdownOpen(!isDropdownOpen)}
                  >
                    <MicroplateIcon className={cs.plateIcon} />
                    Plate Actions
                  </a>
                }
                open={isDropdownOpen}
                onClose={() => setDropdownOpen(false)}
              />
            )}
          </div>
        ) : (
          <div className={cs.heroToolbar}>
            <div className={cs.selectionHeader}>
              <div className={cs.selectionTitle}>
                Selecting wells for passage ({selectionState.selectedWells.length} / 96)
              </div>
              <div className={cs.heroLabel}>
                Auto-selected 72 wells with &gt;70% confluence
              </div>
            </div>
            <div className={cs.plateActions}>
              <Button
                className={cs.cancelButton}
                onClick={handleCancelPassage}
                label='Cancel'
              />
              <Button
                className={cs.submitButton}
                onClick={handleSubmitPassage}
                label='Submit Passage'
                type='primary'
              />
            </div>
          </div>
        )}

        <div className={cs.unusedArea}></div>

        <div>
          <PlateHero
            plate={plate}
            selectedWells={selectionState.selectedWells}
            onWellToggle={
              selectionState.selectedWells === undefined ? undefined : handleWellToggle
            }
            wellOverlay={{
              key: 'status',
              label: 'Culture Status',
            }}
            routePrefix={routePrefix}
            observationTimestamp={latestPlateObservationTime}
          />
        </div>

        <div>
          <PlateConfluenceGraphSidebar plate={plate} />
          <PlateMetadataSidebar plate={plate} />
        </div>
      </div>
    </div>
  )
}

function _LoadingPlaceholder({ id, routePrefix }: { id: string; routePrefix: string }) {
  // TODO(SWE-1840): We don't cache responses yet in backend v2, but once we do,
  // we should render the cached data here when possible.

  return (
    <div>
      <BreadcrumbsCle2025 plate={{ id, data: null }} routePrefix={routePrefix} />

      <div className={cs.main}>
        <div className={cs.title}>
          <ZeroWidthSpaceToken />
        </div>

        <div className={cs.heroLabel}>&nbsp;</div>

        {/* Empty placeholder for plate hero grid area */}
        <div />
      </div>
    </div>
  )
}

function PlateStatus({ plate }: { plate: PlatePage_CulturePlate }) {
  const statuses = new Set(plate.wellCultures.map(c => c.status))

  // All wells are checked out
  if (!plate.isCheckedIn) {
    return <span className={cs.status}>Checked Out</span>
  }

  // Some wells are active
  if (statuses.has('Active')) {
    return <span className={cx(cs.status, cs.checkedIn)}>Checked In</span>
  }

  // All wells are either consumed or terminated
  return <span className={cs.status}>Inactive</span>
}
